import {
  DraftConjointItem,
  DraftTextHighlighterItem
} from '../../../data/gql-gen/questionnaire/graphql'
import {
  DraftCustomAudience,
  FieldworkAudience,
  FieldworkAudienceType
} from '../../../data/model/audience'
import {
  DraftMatrixItem,
  DraftQuestionItem,
  DraftQuestionnaireEntry,
  DraftTextCardItem,
  EntryType
} from '../../../data/model/questionnaire'

// Returns the set of language codes for which there is at least one stored
// translation across the supplied questionnaire entries and audience members.
// Used to seed the declared-languages list so that any language with existing
// translations stays visible in the sidebar even if it was never added via
// the explicit "Add language" action.
export const languagesInUse = (
  entries: ReadonlyArray<DraftQuestionnaireEntry>,
  audienceMembers: ReadonlyArray<FieldworkAudience>
): Set<string> => {
  const codes = new Set<string>()

  const addAll = (
    translations: ReadonlyArray<{ language: string }> | null | undefined
  ) => {
    translations?.forEach((t) => {
      if (t.language) codes.add(t.language)
    })
  }

  entries.forEach((entry) => {
    switch (entry.entryType) {
      case EntryType.QuestionEntryType: {
        const item = entry.entryItem as DraftQuestionItem
        addAll(item.question?.translations)
        addAll(item.maxDiffLabelTranslations)
        item.questionScale?.labels.forEach((label) =>
          addAll(label.labelTranslations)
        )
        item.responseOptions.forEach((opt) =>
          addAll(opt.responseOption?.translations)
        )
        break
      }
      case EntryType.MatrixEntryType: {
        const item = entry.entryItem as DraftMatrixItem
        addAll(item.translations)
        item.matrixRows.forEach((row) => addAll(row.question?.translations))
        item.responseOptions.forEach((opt) =>
          addAll(opt.responseOption?.translations)
        )
        break
      }
      case EntryType.TextCardEntryType: {
        const item = entry.entryItem as DraftTextCardItem
        addAll(item.translations)
        break
      }
      case EntryType.TextHighlighterEntryType: {
        const item = entry.entryItem as DraftTextHighlighterItem
        addAll(item.translations)
        item.highlighterCategories.forEach((category) =>
          addAll(category.responseOption.translations)
        )
        break
      }
      case EntryType.ConjointEntryType: {
        const item = entry.entryItem as DraftConjointItem
        addAll(item.translations)
        addAll(item.attributeTranslations)
        break
      }
      default:
        break
    }
  })

  audienceMembers.forEach((member) => {
    if (member.memberType !== FieldworkAudienceType.Custom) return
    const item = member.memberItem as DraftCustomAudience
    addAll(item.question.translations)
    item.criterionOptions.forEach((opt) =>
      addAll(opt.responseOption.translations)
    )
  })

  return codes
}
